/**
 * AlertNotificationBell - System Alerts Bell Icon
 * 
 * Shows anomaly detection alerts in header.
 * Features:
 * - Badge showing unread count (critical/warning/info)
 * - Dropdown with recent alerts
 * - Link to full alerts page
 * - Manual scan trigger
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, AlertCircle, AlertTriangle, Info, RefreshCw, ExternalLink, Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  getAlerts,
  getAlertsSummary,
  scanForAlerts,
  markAlertRead,
  dismissAlert,
  markAllAlertsRead
} from '../../api/alerts';

const AlertNotificationBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get alert summary (counts)
  const { data: summaryData } = useQuery({
    queryKey: ['alertsSummary'],
    queryFn: getAlertsSummary,
    refetchInterval: 60000, // Poll every minute
    staleTime: 30000
  });

  // Get recent alerts when dropdown is open
  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['recentAlerts'],
    queryFn: () => getAlerts({ limit: 8, isDismissed: false }),
    enabled: isOpen,
    staleTime: 10000
  });

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: scanForAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentAlerts'] });
    }
  });

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: markAlertRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentAlerts'] });
    }
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: dismissAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentAlerts'] });
    }
  });

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: markAllAlertsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentAlerts'] });
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAlertClick = (alert) => {
    if (!alert.is_read) {
      markReadMutation.mutate(alert.id);
    }
    if (alert.action_url) {
      navigate(alert.action_url);
      setIsOpen(false);
    }
  };

  const handleDismiss = (e, alertId) => {
    e.stopPropagation();
    dismissMutation.mutate(alertId);
  };

  const summary = summaryData?.data || { critical: 0, warning: 0, info: 0, total: 0 };
  const alerts = alertsData?.data?.alerts || [];

  // Determine badge color based on highest severity
  const getBadgeColor = () => {
    if (summary.critical > 0) return 'bg-red-500';
    if (summary.warning > 0) return 'bg-yellow-500';
    if (summary.info > 0) return 'bg-blue-500';
    return 'bg-gray-400';
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getSeverityBorderColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="System Alerts"
        title="Anomaly Detection Alerts"
      >
        <Bell className="h-5 w-5" />

        {/* Unread Badge */}
        {summary.total > 0 && (
          <span className={cn(
            "absolute top-0.5 right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white rounded-full px-1",
            getBadgeColor()
          )}>
            {summary.total > 99 ? '99+' : summary.total}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[400px] bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">System Alerts</h3>
              {summary.total > 0 && (
                <div className="flex items-center gap-1">
                  {summary.critical > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
                      {summary.critical} critical
                    </span>
                  )}
                  {summary.warning > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-100 text-yellow-700 rounded">
                      {summary.warning} warning
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => scanMutation.mutate()}
                disabled={scanMutation.isPending}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                title="Scan for new alerts"
              >
                <RefreshCw className={cn("h-4 w-4", scanMutation.isPending && "animate-spin")} />
              </button>
              {summary.total > 0 && (
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Summary Pills */}
          {summary.total > 0 && (
            <div className="px-4 py-2 border-b border-gray-100 flex gap-2">
              <span className="text-xs text-gray-500">Showing unread alerts:</span>
            </div>
          )}

          {/* Alerts List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-sm font-medium text-gray-900">All clear!</p>
                <p className="text-xs text-gray-500 mt-1">No anomalies detected</p>
                <button
                  onClick={() => scanMutation.mutate()}
                  disabled={scanMutation.isPending}
                  className="mt-3 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Run scan now
                </button>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => handleAlertClick(alert)}
                  className={cn(
                    "px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors border-l-4",
                    getSeverityBorderColor(alert.severity),
                    !alert.is_read && "bg-opacity-50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getSeverityIcon(alert.severity)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          "text-sm line-clamp-1",
                          !alert.is_read ? "font-semibold text-gray-900" : "font-medium text-gray-700"
                        )}>
                          {alert.title}
                        </p>
                        <button
                          onClick={(e) => handleDismiss(e, alert.id)}
                          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                          title="Dismiss"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                        {alert.message}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-gray-400">
                          {getRelativeTime(alert.created_at)}
                        </span>
                        {alert.action_url && (
                          <span className="text-[10px] text-blue-600 flex items-center gap-0.5">
                            {alert.action_label || 'View'}
                            <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              onClick={() => {
                navigate('/alerts');
                setIsOpen(false);
              }}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-1 rounded hover:bg-blue-50 transition-colors"
            >
              View all alerts
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertNotificationBell;
